import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../../components/Navbar';
// Force CSS refresh - update import timestamp
import '../../styles/Course.css?v=123461';
import { courses } from './Academy';
import { FaCheck, FaArrowLeft, FaArrowRight, FaBook, FaCalendarAlt, FaChartLine, FaChevronDown, FaTrophy, FaDownload, FaTimes } from 'react-icons/fa';
import html2canvas from 'html2canvas';

// Helper function to generate confetti elements
const generateConfetti = (count) => {
  const confetti = [];
  for (let i = 0; i < count; i++) {
    const left = Math.random() * 100;
    const color = [
      '#7C3AED', '#4F46E5', '#10B981', '#3B82F6', 
      '#EC4899', '#F59E0B', '#EF4444', '#8B5CF6'
    ][Math.floor(Math.random() * 8)];
    
    const style = {
      left: `${left}%`,
      backgroundColor: color,
      animationDelay: `${Math.random() * 3}s`,
      '--drift': Math.random(),
      '--spin': Math.random() * 2 - 1
    };
    
    confetti.push(<div key={i} className="confetti" style={style} />);
  }
  return confetti;
};

// Parse the course content into structured data
const courseContent = {
  'ethereum-101': {
    'Introduction to Ethereum Trading': {
      'What is Ethereum?': `
        <h2>What is Ethereum?</h2>
        <p>Ethereum is a global, decentralized software platform powering thousands of applications and a vibrant ecosystem of digital assets. Unlike Bitcoin which primarily functions as digital money, Ethereum is a programmable blockchain - a platform where developers build decentralized finance (DeFi) apps, games, NFT marketplaces, and much more.</p>
        
        <p>At its core, Ethereum has these key components:</p>
        <ul>
          <li><strong>Smart Contracts</strong> - Self-executing code that runs on the blockchain, enabling trustless applications</li>
          <li><strong>Ether (ETH)</strong> - The native cryptocurrency used to pay for transactions and computational services</li>
          <li><strong>Decentralized Apps (dApps)</strong> - Applications that run on the Ethereum network rather than a single server</li>
          <li><strong>EVM</strong> (Ethereum Virtual Machine) - The computational engine behind Ethereum that processes smart contracts</li>
        </ul>
        
        <p>Launched in 2015 by Vitalik Buterin and a team of co-founders, Ethereum has grown to become the second-largest cryptocurrency by market capitalization, with thousands of developers and millions of users worldwide.</p>
        
        <blockquote>
          <p>"Ethereum is a decentralized platform that runs smart contracts: applications that run exactly as programmed without any possibility of downtime, censorship, fraud or third-party interference." — Ethereum Foundation</p>
        </blockquote>
        
        <p>Anyone with a crypto wallet can buy, sell, or use ETH on the network, and the entire system operates without central oversight. This makes Ethereum fundamentally different from traditional financial systems where banks and institutions serve as intermediaries.</p>
      `,
      'Why ETH Matters': `
        <h2>Why ETH Matters</h2>
        <p>ETH is widely used as a base currency for trading other tokens. Many tokens (ERC-20 tokens) run on Ethereum, so you often trade them against ETH. Ethereum's technology also underpins emerging crypto trends (like DeFi and NFTs), making ETH central to the crypto ecosystem. In short, learning ETH trading gives you a foundation to explore countless other crypto assets.</p>
        
        <h3>The Foundation of the Token Economy</h3>
        <p>Most cryptocurrencies and tokens today are built on Ethereum as "ERC-20 tokens" - a standard that allows for easy creation and exchange of custom assets. When you trade these tokens, you're often trading against ETH as the base pair. Understanding ETH means understanding the foundation of thousands of crypto projects.</p>
        
        <h3>Powering DeFi (Decentralized Finance)</h3>
        <p>Ethereum hosts most of the DeFi ecosystem - protocols that provide financial services without intermediaries. These include:</p>
        <ul>
          <li>Lending platforms like Aave and Compound</li>
          <li>Decentralized exchanges like Uniswap</li>
          <li>Stablecoins like DAI</li>
          <li>Derivatives protocols like Synthetix</li>
        </ul>
        
        <p>As the backbone of DeFi, ETH is often required as collateral, for transaction fees, or as a trading pair. The value proposition of ETH is therefore deeply connected to the growth of decentralized applications.</p>
        
        <h3>Value Beyond Speculation</h3>
        <p>Unlike many cryptocurrencies that are primarily speculative assets, ETH has utility value: it's required to use the Ethereum network for applications, smart contracts, and transactions. This utility creates actual demand beyond trading.</p>
        
        <p>For traders, this means ETH price movements can sometimes be explained by network activity and adoption rather than just market sentiment.</p>
      `,
      'Basic Trading Concepts': `
        <h2>Basic Trading Concepts</h2>
        <p>To trade ETH, you need a crypto exchange or marketplace and a wallet. You can use centralized exchanges (like Coinbase or Binance) where a company manages the trades, or decentralized ones (more on these soon) where you trade from your own wallet. You should understand terms like volatility (crypto prices can swing rapidly) and liquidity (how easily you can buy/sell without big price changes). Also, Ethereum transactions cost gas fees (paid in ETH) for the computational work; this means whenever you send ETH or trade tokens on Ethereum, a small ETH fee is required.</p>
        
        <h3>Exchange Types</h3>
        <p>There are two main types of exchanges where you can trade Ethereum:</p>
        
        <ul>
          <li><strong>Centralized Exchanges (CEX)</strong> - Platforms like Coinbase, Binance, and Kraken where a company holds your funds and facilitates trades. These are easier for beginners but require trusting the exchange with your assets.</li>
          <li><strong>Decentralized Exchanges (DEX)</strong> - Platforms like Uniswap and dYdX where trades happen directly from your wallet through smart contracts. These offer more privacy and control but have a steeper learning curve.</li>
        </ul>
        
        <h3>Key Trading Terms</h3>
        <ul>
          <li><strong>Market Order</strong> - Buying or selling ETH immediately at the current market price</li>
          <li><strong>Limit Order</strong> - Setting a specific price at which you want to buy or sell ETH</li>
          <li><strong>Stop Loss</strong> - An order to sell ETH when it reaches a certain lower price, limiting your potential loss</li>
          <li><strong>Take Profit</strong> - An order to sell ETH when it reaches a certain higher price, securing your gains</li>
          <li><strong>Trading Pair</strong> - The two assets being exchanged (e.g., ETH/USD, ETH/BTC)</li>
        </ul>
        
        <h3>Volatility and Risk Management</h3>
        <p>Cryptocurrency markets, including Ethereum, are known for their volatility - prices can change dramatically in short periods. This creates both opportunity and risk:</p>
        
        <ul>
          <li>ETH might move 5-10% in a single day (or even hour) during active markets</li>
          <li>Historical crashes have seen ETH lose 50-90% of its value in bear markets</li>
          <li>Bull runs have seen ETH gain hundreds or thousands of percent</li>
        </ul>
        
        <p>For this reason, many experienced traders follow the principle: <strong>Never invest more than you can afford to lose</strong>. Risk management is the key to long-term survival in crypto markets.</p>
      `,
      'Understanding Gas Fees': `
        <h2>Understanding Gas Fees</h2>
        <p>Gas fees are essential transaction costs in the Ethereum network. They are paid in ETH and required for all transactions. The fee consists of a base fee (network-determined minimum) and a priority fee (optional tip to miners). Gas fees vary based on network usage - during high demand, fees increase. You can optimize costs by timing transactions during low network usage or using Layer 2 solutions.</p>
        
        <h3>Why Gas Fees Exist</h3>
        <p>Ethereum's blockchain requires computational resources to process transactions and execute smart contracts. Gas fees serve three important purposes:</p>
        
        <ul>
          <li>Compensating validators (formerly miners) who secure the network and process transactions</li>
          <li>Preventing spam and denial-of-service attacks by making it costly to flood the network</li>
          <li>Allocating the finite computational resources of the blockchain efficiently</li>
        </ul>
        
        <h3>How Gas Fees Work</h3>
        <p>Gas fees are calculated using "gas units" - a measure of computational effort. Different operations require different amounts of gas:</p>
        
        <ul>
          <li>A simple ETH transfer typically costs 21,000 gas units</li>
          <li>Complex smart contract interactions can cost hundreds of thousands of gas units</li>
          <li>The actual ETH cost is: gas units × gas price (in gwei, where 1 gwei = 0.000000001 ETH)</li>
        </ul>
        
        <h3>Managing Gas Costs</h3>
        <p>For traders, especially those working with smaller amounts, gas fees can significantly impact profitability. Here are strategies to manage gas costs:</p>
        
        <ul>
          <li><strong>Timing</strong>: Trade during off-peak hours when network congestion is lower</li>
          <li><strong>Gas trackers</strong>: Use services like ETH Gas Station to monitor current gas prices</li>
          <li><strong>Layer 2 solutions</strong>: Consider trading on Ethereum scaling solutions like Optimism, Arbitrum, or Polygon</li>
          <li><strong>Batching</strong>: Group multiple transactions together when possible</li>
        </ul>
        
        <p>Since Ethereum's transition to Proof of Stake in 2022 (The Merge), and subsequent network upgrades, gas fees have become more predictable but still remain a significant consideration for active traders.</p>
      `,
    },
    'Swapping and Bridging': {
      'DEX Trading Basics': `Swapping on decentralized exchanges (DEXs) like Uniswap is a common way to trade ETH and tokens. Unlike traditional exchanges, DEXs use liquidity pools and smart contracts to enable peer-to-peer trading. You connect your wallet, select tokens to swap, and the DEX handles the exchange automatically.`,
      'Using Liquidity Pools': `Liquidity pools are smart contracts holding pairs of tokens that enable DEX trading. When you swap tokens, you're trading against these pools. The price is determined by the ratio of tokens in the pool - as one token is bought more, its price in the pool increases.`,
      'Cross-chain Bridges': `Blockchain bridges allow you to transfer assets between different networks, like moving ETH from Ethereum to Polygon. The bridge locks your tokens on the source chain and mints equivalent tokens on the target chain. This is useful for accessing different DeFi ecosystems or avoiding high gas fees.`,
      'Managing Transaction Costs': `To manage transaction costs effectively, monitor gas prices using tools like ETH Gas Station. Consider using Layer 2 solutions or side chains for frequent trading. Some DEXs also offer gas optimization features like transaction batching.`
    },
    'Essential Tools and Security': {
      'Wallets and Storage': `A secure wallet is crucial for trading. Hardware wallets (like Ledger) offer maximum security for long-term holdings. Software wallets like MetaMask are convenient for active trading. Never share your private keys or seed phrase - they control access to your funds.`,
      'Block Explorers': `Block explorers like Etherscan let you track transactions, verify smart contracts, and monitor wallet activities. They're essential tools for confirming transactions and researching tokens or projects.`,
      'Price Tracking Tools': `Use price tracking tools like CoinGecko or DEX-specific tools like DexTools to monitor prices, trading volume, and liquidity. These help inform trading decisions and identify potential opportunities or risks.`,
      'Security Best Practices': `Practice good security: use strong passwords, enable 2FA where possible, verify contract addresses before interacting, and never trust unsolicited messages or offers. Be especially careful with new or unaudited projects.`
    },
    'Risk Management': {
      'Common Crypto Scams': `Be aware of common scams like rug pulls (developers abandoning projects with investor funds), fake tokens, and phishing attempts. Always verify projects and contracts before investing.`,
      'Safe Trading Practices': `Set clear entry and exit points for trades. Don't invest more than you can afford to lose. Consider using stop-loss orders to limit potential losses. Diversify your portfolio to spread risk.`,
      'Portfolio Management': `Track your portfolio's performance and rebalance when needed. Consider both short-term trades and long-term holdings. Keep records of trades for tax purposes.`,
      'Market Analysis Basics': `Learn to read market indicators and trends. Understand both technical analysis (price charts, patterns) and fundamental analysis (project development, adoption metrics).`
    }
  }
};

// Certificate Component
const Certificate = ({ isOpen, onClose, course, completedLessons, userName }) => {
  const certificateRef = useRef(null);
  const [confetti, setConfetti] = useState([]);
  const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const completionDate = new Date().toLocaleDateString(undefined, dateOptions);
  
  useEffect(() => {
    if (isOpen) {
      setConfetti(generateConfetti(40));
    }
  }, [isOpen]);
  
  const handleDownload = async () => {
    if (certificateRef.current) {
      try {
        const canvas = await html2canvas(certificateRef.current);
        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `${course.title}_Certificate.png`;
        link.href = dataUrl;
        link.click();
      } catch (error) {
        console.error('Error generating certificate:', error);
        alert('Could not download certificate. Please try again.');
      }
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="certificate-overlay">
      {confetti}
      <div className="certificate-modal">
        <button className="certificate-close" onClick={onClose}>
          <FaTimes />
        </button>
        
        <div className="certificate-content" ref={certificateRef}>
          <div className="certificate-logo">
            THE FIRM ACADEMY
          </div>
          
          <div className="certificate-badge">
            <FaTrophy />
          </div>
          
          <h1 className="certificate-title">Certificate of Completion</h1>
          <p className="certificate-subtitle">This certifies that</p>
          
          <h2 className="certificate-name">{userName}</h2>
          
          <p className="certificate-course">
            Successfully completed<br/>
            <strong>{course.title}</strong>
          </p>
          
          <p className="certificate-date">Completed on {completionDate}</p>
          
          <p className="certificate-message">
            Congratulations on completing all lessons!
            Your dedication to learning is commendable.
          </p>
          
          <div className="certificate-signature">
            <div className="signature-block">
              <div className="signature-line"></div>
              <p className="signature-name">THE FIRM Core Team</p>
              <p className="signature-title">FIRM Academy</p>
            </div>
          </div>
        </div>
        
        <div className="certificate-buttons">
          <button 
            className="certificate-button primary"
            onClick={handleDownload}
            style={{
              background: 'linear-gradient(45deg, #7C3AED, #4F46E5)',
              color: 'white',
              border: 'none',
            }}
          >
            <FaDownload /> Download Certificate
          </button>
        </div>
      </div>
    </div>
  );
};

// Name Input Modal Component
const NameInputModal = ({ isOpen, onSubmit, onCancel }) => {
  const [name, setName] = useState('');
  
  if (!isOpen) return null;
  
  return (
    <div className="certificate-overlay">
      <div className="certificate-modal name-input-modal">
        <h2 style={{ 
          color: 'white', 
          marginBottom: '15px', 
          textAlign: 'center',
          fontSize: '1.3rem' 
        }}>
          Congratulations!
        </h2>
        
        <p style={{ 
          color: 'rgba(255, 255, 255, 0.8)', 
          marginBottom: '15px', 
          textAlign: 'center',
          fontSize: '0.95rem'
        }}>
          Enter your name for the certificate:
        </p>
        
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            className="name-input-field"
          />
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <button
            onClick={onCancel}
            style={{
              flex: '1',
              padding: '8px 12px',
              borderRadius: '8px',
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(name || 'Academy Graduate')}
            style={{
              flex: '1',
              padding: '8px 12px',
              borderRadius: '8px',
              background: 'linear-gradient(45deg, #7C3AED, #4F46E5)',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

const Course = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isGuestUser, setGuestUser } = useAuth();
  
  console.log("Course component rendering with courseId:", courseId);
  console.log("Available courses:", courses);
  
  // Find the course by ID instead of direct access
  const course = courses.find(c => c.id === courseId);
  console.log("Found course:", course);

  const [currentModule, setCurrentModule] = useState(0);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [allCompleted, setAllCompleted] = useState(false);
  const [userName, setUserName] = useState('');
  
  const mobileNavRef = useRef(null);
  
  // Wrap checkAllCompleted in useCallback to prevent it from changing on every render
  const checkAllCompleted = useCallback((completedList) => {
    if (!course) return;
    
    let totalLessons = 0;
    course.modules.forEach((module) => {
      totalLessons += module.lessons.length;
    });
    
    setAllCompleted(completedList.length >= totalLessons);
  }, [course]);

  useEffect(() => {
    if (!course) {
      console.log("Course not found, redirecting to Academy");
      navigate('/academy');
      return;
    }

    // Check access rights - allow guest users for ethereum-101 course
    if (courseId === 'ethereum-101') {
      // Allow access for everyone to ethereum-101
      if (!currentUser && !isGuestUser()) {
        setGuestUser(); // Auto-set guest user for ethereum-101
      }
    } else if (!course.isGuestAccessible && !currentUser && !isGuestUser()) {
      navigate('/academy');
      return;
    }

    // Load completed lessons from localStorage
    const savedProgress = localStorage.getItem(`course_progress_${courseId}`);
    if (savedProgress) {
      const loaded = JSON.parse(savedProgress);
      setCompletedLessons(loaded);
      
      // Check if all lessons are completed
      checkAllCompleted(loaded);
    }
    
    // Close mobile nav when clicking outside
    const handleClickOutside = (event) => {
      if (mobileNavRef.current && !mobileNavRef.current.contains(event.target)) {
        setMobileNavOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [courseId, currentUser, isGuestUser, navigate, course, setGuestUser, checkAllCompleted]);
  
  const handleLessonComplete = () => {
    const lessonKey = `${currentModule}-${currentLesson}`;
    if (!completedLessons.includes(lessonKey)) {
      const newCompletedLessons = [...completedLessons, lessonKey];
      setCompletedLessons(newCompletedLessons);
      localStorage.setItem(
        `course_progress_${courseId}`,
        JSON.stringify(newCompletedLessons)
      );
      
      // Check if this was the last lesson
      checkAllCompleted(newCompletedLessons);
      
      // If this completes the course, show name input after a short delay
      setTimeout(() => {
        if (newCompletedLessons.length >= course.totalLessons) {
          setShowNameInput(true);
        }
      }, 1000);
    }
  };

  const isLessonCompleted = (moduleIndex, lessonIndex) => {
    return completedLessons.includes(`${moduleIndex}-${lessonIndex}`);
  };

  const handleNextLesson = () => {
    const currentModuleData = course.modules[currentModule];
    // If not the last lesson in the module, go to the next lesson
    if (currentLesson < currentModuleData.lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
    } 
    // If last lesson in the module but not the last module, go to the first lesson of the next module
    else if (currentModule < course.modules.length - 1) {
      setCurrentModule(currentModule + 1);
      setCurrentLesson(0);
    }
    // If we're at the very last lesson and everything is completed, show name input
    else if (allCompleted) {
      setShowNameInput(true);
    }
  };

  const handlePrevLesson = () => {
    // If not the first lesson in the module, go to the previous lesson
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
    } 
    // If first lesson in the module but not the first module, go to the last lesson of the previous module
    else if (currentModule > 0) {
      setCurrentModule(currentModule - 1);
      setCurrentLesson(course.modules[currentModule - 1].lessons.length - 1);
    }
  };
  
  const viewCertificate = () => {
    if (allCompleted) {
      setShowNameInput(true);
    }
  };
  
  const handleNameSubmit = (name) => {
    setUserName(name);
    setShowNameInput(false);
    setShowCertificate(true);
  };

  if (!course) {
    return <div>Course not found</div>;
  }

  const currentModuleData = course.modules[currentModule];
  const currentLessonTitle = currentModuleData?.lessons[currentLesson];
  const currentLessonContent = courseContent[courseId]?.[currentModuleData?.title]?.[currentLessonTitle];
  
  console.log("Current module data:", currentModuleData);
  console.log("Current lesson title:", currentLessonTitle);
  console.log("Current lesson content:", currentLessonContent);

  // Calculate if we're at the first or last lesson for navigation purposes
  const isFirstLesson = currentModule === 0 && currentLesson === 0;
  const isLastLesson = currentModule === course.modules.length - 1 && 
                       currentLesson === currentModuleData.lessons.length - 1;
  
  // Calculate progress percentage
  const progressPercentage = Math.round((completedLessons.length / course.totalLessons) * 100);

  return (
    <div className="course-page">
      <Navbar />
      
      <header className="course-header">
        <h1 className="course-title">{course.title}</h1>
          <div className="course-meta">
          <span title="Number of modules"><FaBook style={{marginRight: '5px'}} /> {course.modules.length} Modules</span>
          <span title="Completed lessons"><FaCheck style={{marginRight: '5px'}} /> {completedLessons.length}/{course.totalLessons}</span>
          <span title="Completion percentage"><FaChartLine style={{marginRight: '5px'}} /> {progressPercentage}%</span>
          <span title="Course duration"><FaCalendarAlt style={{marginRight: '5px'}} /> {course.duration}</span>
          {allCompleted && (
            <span 
              title="View Certificate" 
              onClick={viewCertificate}
              style={{cursor: 'pointer', color: '#10B981', fontWeight: 'bold'}}
            >
              <FaTrophy style={{marginRight: '5px'}} /> Certificate
            </span>
          )}
        </div>
      </header>

      {/* Mobile-only course navigator */}
      <div className="mobile-course-nav" ref={mobileNavRef}>
        <div 
          className="mobile-nav-current" 
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
        >
          <div>
            <div>{currentModuleData.title}</div>
            <div style={{fontSize: '0.8rem', opacity: 0.7}}>{currentLessonTitle}</div>
          </div>
          <FaChevronDown style={{transform: mobileNavOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s'}} />
        </div>
        
        <div className={`mobile-nav-dropdown ${mobileNavOpen ? 'active' : ''}`}>
          {course.modules.map((module, moduleIndex) => (
            <div key={moduleIndex} className="mobile-module-item">
              <div className="mobile-module-title">
                <span>{module.title}</span>
                <span style={{fontSize: '0.8rem', opacity: 0.7}}>{module.lessons.length} Lessons</span>
              </div>
              <div className="mobile-lesson-list">
                {module.lessons.map((lesson, lessonIndex) => (
                  <div
                    key={lessonIndex}
                    className={`mobile-lesson-item ${
                      moduleIndex === currentModule && lessonIndex === currentLesson
                        ? 'active'
                        : ''
                    }`}
                    onClick={() => {
                      setCurrentModule(moduleIndex);
                      setCurrentLesson(lessonIndex);
                      setMobileNavOpen(false);
                    }}
                  >
                    <span>{lessonIndex + 1}.</span>
                    <span style={{flex: 1}}>{lesson}</span>
                    {isLessonCompleted(moduleIndex, lessonIndex) && (
                      <FaCheck style={{color: '#10B981', fontSize: '0.8rem'}} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mobile-nav-progress">
          <div 
            className="mobile-nav-progress-bar" 
            style={{width: `${progressPercentage}%`}}
          ></div>
        </div>
      </div>

      <div className="course-content">
        {/* Desktop Sidebar */}
        <aside className="course-sidebar">
          {course.modules.map((module, moduleIndex) => (
            <div key={moduleIndex} className="module-section">
              <div className="module-header">
                <h3>{module.title}</h3>
                <span className="lesson-count">{module.lessons.length} Lessons</span>
              </div>
              <div className="lessons-list">
                {module.lessons.map((lesson, lessonIndex) => (
                  <div
                    key={lessonIndex}
                    className={`lesson-item ${
                      moduleIndex === currentModule && lessonIndex === currentLesson
                        ? 'active'
                        : ''
                    }`}
                    onClick={() => {
                      setCurrentModule(moduleIndex);
                      setCurrentLesson(lessonIndex);
                    }}
                  >
                    <span className="lesson-number">{lessonIndex + 1}</span>
                    <span className="lesson-title">{lesson}</span>
                    {isLessonCompleted(moduleIndex, lessonIndex) && (
                      <FaCheck className="completion-check" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <main className="lesson-main">
        <div className="lesson-content">
          <div className="lesson-header">
            <h2>{currentLessonTitle}</h2>
            <button 
              className="complete-button"
                onClick={handleLessonComplete}
                disabled={isLessonCompleted(currentModule, currentLesson)}
              >
                {isLessonCompleted(currentModule, currentLesson)
                  ? 'Completed'
                  : 'Mark as Complete'}
              </button>
            </div>
            <div className="lesson-body">
              {currentLessonContent && (
                <div 
                  className="lesson-content-html"
                  dangerouslySetInnerHTML={{ __html: currentLessonContent }}
                />
              )}
              
              <div className="lesson-navigation">
                <button 
                  className="lesson-nav-button prev" 
                  onClick={handlePrevLesson}
                  disabled={isFirstLesson}
                  style={{ visibility: isFirstLesson ? 'hidden' : 'visible' }}
                >
                  <FaArrowLeft style={{marginRight: '5px'}} /> Previous Lesson
                </button>
                <button 
                  className="lesson-nav-button next" 
                  onClick={handleNextLesson}
                  disabled={isLastLesson && !allCompleted}
                  style={isLastLesson && !allCompleted ? {visibility: 'hidden'} : {}}
                >
                  {isLastLesson && allCompleted ? (
                    <>View Certificate <FaTrophy style={{marginLeft: '5px'}} /></>
                  ) : (
                    <>Next Lesson <FaArrowRight style={{marginLeft: '5px'}} /></>
                  )}
            </button>
          </div>
          
              {allCompleted && (
                <div style={{
                  textAlign: 'center', 
                  marginTop: '30px', 
                  padding: '20px', 
                  background: 'rgba(16, 185, 129, 0.1)', 
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}>
                  <FaTrophy style={{fontSize: '2rem', color: '#10B981', marginBottom: '10px'}} />
                  <h3 style={{color: 'white', marginBottom: '10px'}}>Congratulations!</h3>
                  <p style={{color: 'rgba(255, 255, 255, 0.8)'}}>
                    You've completed all lessons in this course. Great job!
                  </p>
                  <button 
                    onClick={viewCertificate}
                    style={{
                      background: 'linear-gradient(45deg, #10B981, #059669)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      marginTop: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FaTrophy /> View Your Certificate
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      
      {/* Name Input Modal */}
      <NameInputModal 
        isOpen={showNameInput}
        onSubmit={handleNameSubmit}
        onCancel={() => setShowNameInput(false)}
      />
      
      {/* Certificate Modal */}
      <Certificate 
        isOpen={showCertificate}
        onClose={() => setShowCertificate(false)}
        course={course}
        completedLessons={completedLessons}
        userName={userName}
      />
    </div>
  );
};

export default Course; 